import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { startArticleReturnTransition } from './article-transition';
import barba from '@barba/core';

gsap.registerPlugin(ScrollTrigger);

export const initArticleScroll = (container, lenis) => {
    const content      = container.querySelector('#article-content');
    const gridItems    = container.querySelectorAll('#article-content .group');
    const panel        = container.querySelector('#article-panel');
    const btnClose     = container.querySelector('#btn-close');
    const panelClose   = container.querySelector('#panel-close');
    const articleLinks = container.querySelectorAll('.panel-article-link');
    const headerEl     = document.querySelector('header');
    const footerEl     = document.querySelector('footer');

    if (!content || !lenis) return;

const PW = panel.getBoundingClientRect().width;

    // ── État initial ──
    gsap.set(panel, { x: -PW });

    let panelOpen = false;

    const openPanel = () => {
        panelOpen = true;
        document.body.style.overflowX = 'hidden';

        gsap.to(panel,     { x: 0,          duration: 0.65, ease: 'power3.inOut' });
        gsap.to(container, { marginLeft: PW, duration: 0.65, ease: 'power3.inOut' });
        gsap.to(headerEl,  { marginLeft: PW, duration: 0.65, ease: 'power3.inOut' });
        gsap.to(footerEl,  { marginLeft: PW, duration: 0.65, ease: 'power3.inOut' });


        gsap.fromTo(articleLinks,
            { opacity: 0, x: -16 },
            { opacity: 1, x: 0, duration: 0.4, stagger: 0.04, ease: 'power2.out', delay: 0.3 }
        );
    };

    const closePanel = () => {
        panelOpen = false;

        gsap.to(panel,     { x: -PW,        duration: 0.5, ease: 'power3.inOut' });
        gsap.to(container, { marginLeft: 0,  duration: 0.5, ease: 'power3.inOut' });
        gsap.to(headerEl,  { marginLeft: 0,  duration: 0.5, ease: 'power3.inOut' });
        gsap.to(footerEl,  { marginLeft: 0,  duration: 0.5, ease: 'power3.inOut' });


        gsap.delayedCall(0.5, () => {
            document.body.style.overflowX = '';
        });
    };

    const resetAll = () => {
        gsap.set(panel,     { x: -PW });
        gsap.set(container, { clearProps: 'marginLeft' });
        gsap.set(headerEl,  { clearProps: 'marginLeft' });
        gsap.set(footerEl,  { clearProps: 'marginLeft' });

        document.body.style.overflowX = '';
    };

    // ── Events ──
    const triggerBtn = container.querySelector('#panel-trigger');
    if (triggerBtn) triggerBtn.addEventListener('click', () => panelOpen ? closePanel() : openPanel());
    if (panelClose)  panelClose.addEventListener('click',  () => closePanel());

    if (btnClose) {
        btnClose.addEventListener('click', (e) => {
            e.preventDefault();
            if (panelOpen) {
                closePanel();
                gsap.delayedCall(0.5, () => startArticleReturnTransition());
            } else {
                startArticleReturnTransition();
            }
        });
    }

    const backBtn = container.querySelector('#article-back-btn');
    if (backBtn) {
        gsap.to(backBtn, { opacity: 1, duration: 0.5, delay: 1 });
        backBtn.addEventListener('click', () => {
            if (panelOpen) {
                closePanel();
                gsap.delayedCall(0.5, () => startArticleReturnTransition());
            } else {
                startArticleReturnTransition();
            }
        });
    }
    articleLinks.forEach((link) => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const url = link.dataset.url || link.href;
            if (!url) return;
            closePanel();
            gsap.delayedCall(0.5, () => barba.go(url));
        });
    });

    // ── Recalcul des déclencheurs quand les images arrivent ──
    // Les images en différé changent la hauteur de la page en se chargeant : sans recalcul,
    // les déclencheurs de la cascade restent mal placés et la galerie reste invisible.
    let refreshTimer = null;
    const scheduleRefresh = () => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => { lenis.resize(); ScrollTrigger.refresh(); }, 150);
    };
    container.querySelectorAll('#article-content img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', scheduleRefresh, { once: true });
    });

    // ── Fondu des images (hero + galerie) au-dessus de leur aperçu flou ──
    container.querySelectorAll('.img-fade').forEach((img) => {
        const show = () => img.classList.add('is-loaded');
        if (img.complete && img.naturalWidth) show();
        else {
            img.addEventListener('load', show, { once: true });
            img.addEventListener('error', show, { once: true });
        }
    });

    // ── Cascade entrée images ──
    if (gridItems.length > 0) {
        gridItems.forEach((item, index) => {
            const innerImg = item.querySelector('.img-projet');

            gsap.set(item, { y: 60, opacity: 0 });

            gsap.to(item, {
                y: 0, opacity: 1,
                duration: 1.5,
                ease: 'expo.out',
                delay: (index % 2) * 0.15,
                scrollTrigger: {
                    trigger: item,
                    start: 'top 95%',
                    toggleActions: 'play reverse play reverse',
                }
            });
        });
    }

    // ── Snap ──
    let snapped = false;
    const onFirstScroll = (e) => {
        const delta = e.deltaY || (e.touches && e.touches[0] ? -1 : 0);
        if (snapped || delta <= 20) return;
        snapped = true;
        lenis.scrollTo(content, {
            duration: 1.4,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            onComplete: () => ScrollTrigger.refresh(),
        });
        removeListeners();
    };

    const removeListeners = () => {
        window.removeEventListener('wheel', onFirstScroll);
        window.removeEventListener('touchstart', onFirstScroll);
    };

    window.addEventListener('wheel', onFirstScroll, { passive: true });
    window.addEventListener('touchstart', onFirstScroll, { passive: true });

    document.addEventListener('barba:leave', () => {
        removeListeners();
        ScrollTrigger.getAll().forEach(t => t.kill());
        resetAll();
    }, { once: true });
};